// screens/ClientPortal.tsx — B18b: what a CLIENT sees, on
// clients.vowric.com.
//
// ⚑ ITS OWN SHELL, not a pack tab. A client is not a member of Huan's
// working life: no bottom nav, no Knot, no Today. One page — who they are
// working with, the conversation, and an honest account of what is not
// built yet.
//
// The conversation is the whole product here. Everything they say lands on
// the ordinary thread spine (through a `portal` door), so Huan reads it
// where he reads everything else and answers from the surfaces he already
// has. Nothing on this page pretends to be a chat app: no typing
// indicators, no read receipts, no presence — a message is a message, and
// the receipt is that it is on the page afterwards.
import { useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  getPortalConversation,
  getPortalMe,
  sayInPortal,
  listPortalLetters,
  answerPortalLetter,
  type PortalMeApi,
  type PortalMessageApi,
  type PortalLetterApi,
} from '../lib/api';
import { SignOutButton } from '../components/SignOutButton';
import './ClientPortal.css';

export interface ClientPortalProps {
  tenantId: string;
  me: PortalMeApi;
}

type LoadState = 'loading' | 'ready' | 'error';

function when(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export function ClientPortal({ tenantId, me }: ClientPortalProps) {
  const [load, setLoad] = useState<LoadState>('loading');
  const [messages, setMessages] = useState<PortalMessageApi[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  // B18f — the letters they have been sent, and the one waiting on them.
  const [letters, setLetters] = useState<PortalLetterApi[]>([]);
  const [asking, setAsking] = useState<string | null>(null);
  const [askText, setAskText] = useState('');

  function refresh() {
    return Promise.all([
      getPortalConversation(tenantId).then((c) => setMessages(c.messages)),
      // A letter surface that fails must not take the conversation down with
      // it — the conversation is the thing they came for.
      listPortalLetters(tenantId).then(setLetters).catch(() => setLetters([])),
    ])
      .then(() => setLoad('ready'))
      .catch(() => setLoad('error'));
  }

  /** Their answer. The only place the other side of this relationship acts. */
  async function answer(letterId: string, which: 'accepted' | 'changes_asked') {
    setBusy(true);
    setNote(null);
    try {
      await answerPortalLetter(tenantId, letterId, {
        answer: which,
        ...(which === 'changes_asked' && askText.trim() ? { response: askText.trim() } : {}),
      });
      setAsking(null);
      setAskText('');
      await refresh();
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'That did not go through — nothing was changed.');
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  async function send() {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    setNote(null);
    try {
      await sayInPortal(tenantId, body);
      setDraft('');
      await refresh();
    } catch (err) {
      // Never a silent failure: if it did not send, the words stay in the
      // box so nothing they wrote is lost.
      setNote(err instanceof ApiError ? err.message : 'That did not send — it is still here, try again.');
    } finally {
      setBusy(false);
    }
  }

  const notYet = Object.entries(me.sections).filter(([, state]) => state !== 'ready');

  return (
    <div class="cportal">
      <header class="cportal__head">
        <div class="cportal__eyebrow">Vowric</div>
        <h1 class="cportal__name">{me.party.name}</h1>
        {/* The engagement's name only when it SAYS something the title did
            not. A party who never gave a business name is titled by their
            address, and the engagement defaults to the same string — three
            copies of one address is what the walk showed. */}
        {me.engagement && me.engagement.name !== me.party.name ? (
          <div class="cportal__engagement">{me.engagement.name}</div>
        ) : null}
      </header>

      {/* ⚑ THE LETTER COMES FIRST WHEN IT IS WAITING ON THEM. It is the one
          thing on this page that is actually asking them for something;
          burying it under a conversation they read every day would be a
          decision to hope they scroll. */}
      {letters.length > 0 ? (
        <section class="cportal__letters">
          <h2 class="cportal__section-title">What we propose to do</h2>
          {letters.map((l) => (
            <article class={`cportal__letter${l.awaitingYou ? ' is-awaiting' : ''}`} key={l.id}>
              <ol class="cportal__letter-items">
                {l.items.map((i) => <li key={i.position}>{i.title}</li>)}
              </ol>
              <div class="cportal__letter-body">{l.bodyMd}</div>
              {l.timingMd ? <div class="cportal__letter-line"><span>When</span>{l.timingMd}</div> : null}
              {l.billingMd ? <div class="cportal__letter-line"><span>Cost</span>{l.billingMd}</div> : null}

              {l.awaitingYou ? (
                asking === l.id ? (
                  <div class="cportal__letter-ask">
                    <textarea
                      class="cportal__letter-asktext"
                      rows={3}
                      placeholder="What would you like changed?"
                      value={askText}
                      disabled={busy}
                      onInput={(e) => setAskText((e.target as HTMLTextAreaElement).value)}
                    />
                    <div class="cportal__letter-actions">
                      <button type="button" class="cportal__btn" disabled={busy}
                        onClick={() => void answer(l.id, 'changes_asked')}>
                        Send that
                      </button>
                      <button type="button" class="cportal__btn" disabled={busy}
                        onClick={() => { setAsking(null); setAskText(''); }}>
                        Never mind
                      </button>
                    </div>
                  </div>
                ) : (
                  <div class="cportal__letter-actions">
                    <button type="button" class="cportal__btn cportal__btn--primary" disabled={busy}
                      onClick={() => void answer(l.id, 'accepted')}>
                      Yes, go ahead
                    </button>
                    <button type="button" class="cportal__btn" disabled={busy}
                      onClick={() => setAsking(l.id)}>
                      Ask for changes
                    </button>
                  </div>
                )
              ) : (
                /* Receipts, not acknowledgements: what they said, and that we
                   have it. */
                <div class="cportal__letter-answered">
                  {l.status === 'accepted' ? 'You said yes to this.' : 'You asked for changes.'}
                  {l.yourResponse ? <span class="cportal__letter-quote">“{l.yourResponse}”</span> : null}
                </div>
              )}
            </article>
          ))}
        </section>
      ) : null}

      <section class="cportal__convo">
        <h2 class="cportal__section-title">Your conversation</h2>

        {load === 'loading' ? (
          <div class="cportal__skeleton" aria-hidden="true">
            <div class="cportal__skeleton-line" />
            <div class="cportal__skeleton-line" />
          </div>
        ) : load === 'error' ? (
          <div class="cportal__say">
            This could not be loaded just now.
            <br />
            <span class="cportal__say-soft">Refresh the page — nothing you sent is lost.</span>
          </div>
        ) : messages.length === 0 ? (
          <div class="cportal__say">
            Nothing here yet.
            <br />
            <span class="cportal__say-soft">
              Anything you write here reaches the person working with you. Documents can come later.
            </span>
          </div>
        ) : (
          <div class="cportal__messages">
            {messages.map((m) => (
              <div class={`cportal__message${m.who === 'you' ? ' is-you' : ''}`} key={m.id}>
                <div class="cportal__message-who">{m.who === 'you' ? 'You' : 'Vowric'}</div>
                <div class="cportal__message-body">{m.body}</div>
                <div class="cportal__message-when">{when(m.occurredAt)}</div>
              </div>
            ))}
          </div>
        )}

        <div class="cportal__composer">
          <textarea
            class="cportal__composer-box"
            rows={3}
            placeholder="Write to us…"
            value={draft}
            disabled={busy}
            onInput={(e) => setDraft((e.target as HTMLTextAreaElement).value)}
          />
          <button
            type="button"
            class="cportal__send"
            disabled={busy || !draft.trim()}
            onClick={() => void send()}
          >
            {busy ? 'Sending…' : 'Send'}
          </button>
        </div>
        {note ? <div class="cportal__note">{note}</div> : null}
      </section>

      {notYet.length > 0 ? (
        <section class="cportal__later">
          <h2 class="cportal__section-title">Not here yet</h2>
          {/* Named, not hidden. A client should never wonder whether they
              missed a step, and should never meet a control that does
              nothing. */}
          {notYet.map(([name]) => (
            <div class="cportal__later-line" key={name}>
              {name === 'documents' ? 'Your documents' : name === 'billing' ? 'Billing and payment' : name}
            </div>
          ))}
        </section>
      ) : null}

      <footer class="cportal__foot">
        <span class="cportal__foot-who">{me.email}</span>
        <SignOutButton />
      </footer>
    </div>
  );
}
